import React, { useMemo, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  IconButton,
  Button,
  CircularProgress,
} from "@mui/material";
import { CloseRounded } from "@mui/icons-material";
import style from "./style";
import { useTranslation } from "react-i18next";
import { useTripContext } from "../../context/tripsContext";
import { tripsService } from "../../api/tripsService/tripsServices";
import { useBusinessProfile } from "../../utils/hooks/useBusinessProfile";
import { useAuth } from "../../utils/hooks/useAuth";

interface TripDialogProps {
  open: boolean;
  onClose: () => void;
  tripId: number | null;
  tmsKey: string | null;
  status?: string | null;
  onUpdate?: (tripId: string, newStatus: string) => void; // Callback per aggiornare lo stato
  gridRef:any

}

const TripDialog: React.FC<TripDialogProps> = ({
  open,
  onClose,
  tripId,
  tmsKey,
  status,
  onUpdate,
  gridRef
}) => {
  const { t } = useTranslation();
  const { trips, setTrips } = useTripContext();
  const { user } = useAuth();
  const {businessProfile} = useBusinessProfile();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [actionType, setActionType] = useState<"APPROVED" | "REJECTED" | null>(
    null
  );

  const formattedUserName = useMemo(() => {
    if (user?.fullName) {
      const arrayFullName = user.fullName.split(" ");
      return arrayFullName
        .map((name) => name.charAt(0).toUpperCase() + name.slice(1).toLowerCase())
        .join(" ");
    }
    return "Unknown User"; // Default se il nome completo non è disponibile
  }, [user]);


  const handleConfirm = async (type:"APPROVED" | "REJECTED") => {
    if (!tripId || typeof tripId !== "number") {
      setError(t("screens.trips.tripDialog.invalidTripId"));
      console.error("Invalid tripId", tripId);
      return;
    }
  
    setLoading(true);
    setError(null);
    setActionType(type);
    try {
      const response = await tripsService.updateTripAcceptance(
        tripId,
        type,
        formattedUserName
      );
        const rowNode = gridRef.current?.api.getRowNode(tmsKey);
  
        if (rowNode) {
          const updatedData = { ...rowNode.data, approval: response.status };
          
          rowNode.setData(updatedData); // Aggiorna senza perdere lo stato precedente
        }
      // Aggiorna il contesto dei viaggi
      const updatedTrips = trips.map((trip) =>
        trip.tripID === response.tripID
          ? { ...trip, approval: response.status }
          : trip
      );
      setTrips(updatedTrips);
    
      
      if (onUpdate) {
        onUpdate(tmsKey? tmsKey : '', type);
      }
      onClose(); // Chiudi il dialog

    } catch (err) {
      console.error("Error in API call:", err);
      setError(t("screens.trips.tripDialog.error"));
    } finally {
      setLoading(false);
    }
  };
  
  const handleDialogClose = (event: React.SyntheticEvent, reason?: string) => {
    // Chiudi sempre il dialog, ignorando il motivo.
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={() =>{}}
      sx={{ "& .MuiDialog-paper": { minWidth: "700px", ...style.dialogBox } }}
    >
      <DialogTitle sx={style.dialogTitleBox}>
        <div style={style.dialogDiv}>
          <Typography sx={style.dialogText}>
           
              { t("screens.trips.tripDialog.tripApproval")}
            
          </Typography>
          <IconButton onClick={() =>onClose()}>
            <CloseRounded sx={style.iconColor} />
          </IconButton>
        </div>
      </DialogTitle>
      <DialogContent sx={style.dialogContent}>
        <Typography variant="body1" mb={2}>
          {t("screens.trips.tripDialog.doYouWantTo")}{" "}
          <strong>
               {t("screens.trips.tripDialog.approve")}
          </strong>{" "}
          {t("screens.trips.tripDialog.thisTrip")} (
          <strong> ID: {tmsKey}</strong> )
        </Typography>
      </DialogContent>
      <DialogActions sx={{ ...style.dialogAction, justifyContent: "right" }}>
      <Button
          variant="outlined"
          sx={style.buttonOutline}
          onClick={() => handleConfirm("REJECTED")}
          disabled={(status !== "ASSIGNED" && status !== "ALLOCATED") || loading }
        >
          {loading && actionType === "REJECTED" ? (
            <CircularProgress size={24} color="inherit" />
          ) : (
            t("screens.trips.tripDialog.reject2")
          )}
        </Button>
        <Button
          variant="contained"
          color="primary"
          onClick={() => handleConfirm("APPROVED")}
          disabled={loading}
        >
          {loading && actionType === "APPROVED" ? (
            <CircularProgress size={24} color="inherit" />
          ) : (
            t("screens.trips.tripDialog.approve2")
          )}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default TripDialog;
